import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Info, Medal, Trophy, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import API_BASE from "../api";
import DesktopRightRail from "../components/DesktopRightRail";
import "../styles/pages/FishipediaRankings.css";

const FALLBACK_AVATAR =
  "https://cdn-icons-png.flaticon.com/512/847/847969.png";
const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";
const INFO_HOW_ITEMS = [
  "fishipedia_rankings_info_how_catch",
  "fishipedia_rankings_info_how_opinion",
  "fishipedia_rankings_info_how_engagement",
];
const INFO_CATCH_DETAIL_ITEMS = [
  "fishipedia_rankings_info_catch_location",
  "fishipedia_rankings_info_catch_media",
  "fishipedia_rankings_info_catch_species",
  "fishipedia_rankings_info_catch_lure",
  "fishipedia_rankings_info_catch_weight",
  "fishipedia_rankings_info_catch_length",
  "fishipedia_rankings_info_catch_rod",
  "fishipedia_rankings_info_catch_reel",
  "fishipedia_rankings_info_catch_leader",
  "fishipedia_rankings_info_catch_braid",
  "fishipedia_rankings_info_catch_hook",
  "fishipedia_rankings_info_catch_knot",
  "fishipedia_rankings_info_catch_caption",
];
const INFO_ENGAGEMENT_ITEMS = [
  "fishipedia_rankings_info_engagement_catch_like",
  "fishipedia_rankings_info_engagement_catch_comment",
  "fishipedia_rankings_info_engagement_catch_share",
  "fishipedia_rankings_info_engagement_opinion_like",
  "fishipedia_rankings_info_engagement_opinion_comment",
  "fishipedia_rankings_info_engagement_opinion_share",
  "fishipedia_rankings_info_engagement_follow",
];
const INFO_GOOD_TO_KNOW_ITEMS = [
  "fishipedia_rankings_info_good_owner",
  "fishipedia_rankings_info_good_actor",
  "fishipedia_rankings_info_good_rank_badge",
  "fishipedia_rankings_info_good_not_yet",
];

function asNonNegativeNumber(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

function normalizeTimestampToMillis(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 1e12 ? parsed * 1000 : parsed;
}

function formatJoinedDate(value) {
  const millis = normalizeTimestampToMillis(value);
  if (!millis) return "";

  try {
    return new Date(millis).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function compareUsersByRanking(leftUser, rightUser) {
  const xpDifference =
    asNonNegativeNumber(rightUser?.progression?.xp) -
    asNonNegativeNumber(leftUser?.progression?.xp);

  if (xpDifference !== 0) {
    return xpDifference;
  }

  const leftCreatedAt = normalizeTimestampToMillis(leftUser?.createdAt);
  const rightCreatedAt = normalizeTimestampToMillis(rightUser?.createdAt);

  if (leftCreatedAt !== rightCreatedAt) {
    if (leftCreatedAt === null) return 1;
    if (rightCreatedAt === null) return -1;
    return leftCreatedAt - rightCreatedAt;
  }

  return String(leftUser?.username || "").localeCompare(
    String(rightUser?.username || ""),
    undefined,
    { sensitivity: "base" }
  );
}

function normalizeAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveAvatarObjectPosition(user) {
  const positionX = normalizeAvatarPosition(user?.avatarPositionX);
  const positionY = normalizeAvatarPosition(user?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function buildBadgeUrl(badgeKey) {
  if (!badgeKey) return "";

  const publicUrl = process.env.PUBLIC_URL || "";
  const normalizedKey = String(badgeKey).trim().toLowerCase();
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function RankingBadge({ badgeKey, rankName }) {
  const { t } = useTranslation();
  const [isMissing, setIsMissing] = useState(false);

  useEffect(() => {
    setIsMissing(false);
  }, [badgeKey]);

  const badgeUrl = buildBadgeUrl(badgeKey);

  return (
    <div
      className="fishipedia-rankings-badge-slot"
      title={rankName || ""}
      aria-hidden={badgeUrl && !isMissing ? "false" : "true"}
    >
      {badgeUrl && !isMissing ? (
        <img
          src={badgeUrl}
          alt={
            rankName
              ? t("fishipedia_rankings_badge_alt", {
                  defaultValue: "{{rankName}} badge",
                  rankName,
                })
              : ""
          }
          className="fishipedia-rankings-badge"
          onError={() => setIsMissing(true)}
        />
      ) : null}
    </div>
  );
}

function getUserDisplayName(user, t) {
  return user?.username || t("user", { defaultValue: "User" });
}

function getUserXp(user) {
  return asNonNegativeNumber(user?.progression?.xp);
}

function getUserRankName(user, t) {
  return (
    user?.progression?.currentRankName ||
    t("progression_rank_default", {
      defaultValue: "Newcomer",
    })
  );
}

function isSameRankedUser(leftUser, rightUser) {
  if (!leftUser || !rightUser) return false;
  if (leftUser.id && rightUser.id && leftUser.id === rightUser.id) return true;
  const leftUsername = String(leftUser.username || "").trim().toLowerCase();
  const rightUsername = String(rightUser.username || "").trim().toLowerCase();
  return Boolean(leftUsername && rightUsername && leftUsername === rightUsername);
}

function RankIcon({ rank }) {
  if (rank === 1) {
    return <Trophy size={15} strokeWidth={2.5} aria-hidden="true" />;
  }

  if (rank === 2 || rank === 3) {
    return <Medal size={15} strokeWidth={2.5} aria-hidden="true" />;
  }

  return null;
}

function LeaderboardRow({ user, rank, t, isCurrentUser = false }) {
  const joinedDate = formatJoinedDate(user?.createdAt);
  const xp = getUserXp(user);
  const rankName = getUserRankName(user, t);

  return (
    <div
      className={`fishipedia-rankings-row${
        isCurrentUser ? " fishipedia-rankings-row--current" : ""
      }`}
    >
      <div
        className={`fishipedia-rankings-rank fishipedia-rankings-rank--${Math.min(
          rank,
          4
        )}`}
        aria-label={t("fishipedia_rankings_rank_label", {
          defaultValue: "Rank {{rank}}",
          rank,
        })}
      >
        <RankIcon rank={rank} />
        <span>{rank}</span>
      </div>

      <img
        src={user?.avatar ? `${API_BASE}${user.avatar}` : FALLBACK_AVATAR}
        alt={getUserDisplayName(user, t)}
        className="fishipedia-rankings-avatar"
        style={{
          objectPosition: resolveAvatarObjectPosition(user),
        }}
      />

      <div className="fishipedia-rankings-user">
        <div className="fishipedia-rankings-username">
          {getUserDisplayName(user, t)}
        </div>
        <div className="fishipedia-rankings-joined">
          {joinedDate
            ? t("fishipedia_rankings_joined", {
                defaultValue: "Joined {{date}}",
                date: joinedDate,
              })
            : t("fishipedia_rankings_joined_unknown", {
                defaultValue: "Joined date unavailable",
              })}
        </div>
      </div>

      <div className="fishipedia-rankings-xp">
        <strong className="fishipedia-rankings-xp-value">
          {new Intl.NumberFormat().format(xp)}
        </strong>
        <span className="fishipedia-rankings-xp-label">
          {t("progression_xp_label", { defaultValue: "XP" })}
        </span>
      </div>

      <RankingBadge
        badgeKey={user?.progression?.currentBadgeKey}
        rankName={rankName}
      />
    </div>
  );
}

function CurrentRankCard({ entry, rank, t }) {
  const xp = getUserXp(entry);
  const rankName = getUserRankName(entry, t);

  return (
    <section
      className="fishipedia-rankings-current"
      aria-label={t("fishipedia_rankings_your_rank", {
        defaultValue: "Your rank",
      })}
    >
      <div className="fishipedia-rankings-current-avatar-wrap">
        <img
          src={entry?.avatar ? `${API_BASE}${entry.avatar}` : FALLBACK_AVATAR}
          alt={getUserDisplayName(entry, t)}
          className="fishipedia-rankings-current-avatar"
          style={{
            objectPosition: resolveAvatarObjectPosition(entry),
          }}
        />
        {rank ? (
          <span className="fishipedia-rankings-current-chip">#{rank}</span>
        ) : null}
      </div>

      <div className="fishipedia-rankings-current-copy">
        <span className="fishipedia-rankings-current-label">
          {t("fishipedia_rankings_your_rank", { defaultValue: "Your rank" })}
        </span>
        <strong>{getUserDisplayName(entry, t)}</strong>
        <span>
          {new Intl.NumberFormat().format(xp)}{" "}
          {t("progression_xp_label", { defaultValue: "XP" })}
        </span>
      </div>

      <RankingBadge
        badgeKey={entry?.progression?.currentBadgeKey}
        rankName={rankName}
      />
    </section>
  );
}

export default function FishipediaRankings({
  currentUser = null,
  posts = [],
  users: rightRailUsers = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isLeaderboardExpanded, setIsLeaderboardExpanded] = useState(false);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  useEffect(() => {
    let isMounted = true;

    async function loadUsers() {
      try {
        setIsLoading(true);
        setError("");

        const response = await fetch(`${API_BASE}/auth/users`, {
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error("Failed to load rankings");
        }

        const data = await response.json();
        if (!isMounted) return;

        setUsers(Array.isArray(data) ? data : []);
      } catch (loadError) {
        if (!isMounted) return;
        setUsers([]);
        setError(
          t("fishipedia_rankings_error", {
            defaultValue: "Unable to load rankings right now.",
          })
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const rankedUsers = useMemo(
    () => [...users].sort(compareUsersByRanking),
    [users]
  );
  const currentUserIndex = useMemo(
    () =>
      rankedUsers.findIndex((rankedUser) =>
        isSameRankedUser(rankedUser, currentUser)
      ),
    [currentUser, rankedUsers]
  );
  const currentRankEntry =
    currentUserIndex >= 0 ? rankedUsers[currentUserIndex] : currentUser;
  const currentRank = currentUserIndex >= 0 ? currentUserIndex + 1 : null;
  const hasAdditionalLeaderboardRows = rankedUsers.length > 5;
  const leadingLeaderboardRows = hasAdditionalLeaderboardRows
    ? rankedUsers.slice(0, 5)
    : rankedUsers;
  const expandedLeaderboardRows =
    hasAdditionalLeaderboardRows && isLeaderboardExpanded
      ? rankedUsers.slice(5)
      : [];

  const closeUtilityPage = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      Number(window.history.state.idx) > 0
    ) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={rightRailUsers}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`fishipedia-rankings-layout${
        enableDesktopRightRail ? " fishipedia-rankings-layout--with-right-rail" : ""
      }`}
    >
      <div className="fishipedia-rankings-page">
        <section className="fishipedia-rankings-shell">
        <div className="fishipedia-rankings-header">
          <div className="fishipedia-rankings-heading-copy">
            <h1>
              {t("fishipedia_rankings_title", {
                defaultValue: "User rankings",
              })}
            </h1>
            <p className="fishipedia-rankings-intro">
              {t("fishipedia_rankings_intro", {
                defaultValue: "See how anglers rank by activity and progression.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="fishipedia-rankings-page-close"
            onClick={closeUtilityPage}
            aria-label={t("common_close")}
          >
            <X size={18} strokeWidth={2.5} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="fishipedia-rankings-learn"
            onClick={() => setIsInfoOpen(true)}
          >
            <Info size={16} strokeWidth={2.4} aria-hidden="true" />
            <span>
              {t("fishipedia_rankings_learn_more", {
                defaultValue: "Learn more",
              })}
            </span>
          </button>
        </div>

        {currentRankEntry && !isLoading && !error ? (
          <CurrentRankCard entry={currentRankEntry} rank={currentRank} t={t} />
        ) : null}

        {isLoading ? (
          <p className="fishipedia-rankings-status">
            {t("fishipedia_loading")}
          </p>
        ) : error ? (
          <p className="fishipedia-rankings-status">{error}</p>
        ) : rankedUsers.length === 0 ? (
          <p className="fishipedia-rankings-status">
            {t("fishipedia_rankings_empty", {
              defaultValue: "No users available yet.",
            })}
          </p>
        ) : (
          <>
            <div className="fishipedia-rankings-list">
              {leadingLeaderboardRows.map((user, index) => (
                <LeaderboardRow
                  key={user.id || user.username || index}
                  user={user}
                  rank={index + 1}
                  t={t}
                  isCurrentUser={isSameRankedUser(user, currentUser)}
                />
              ))}
              {hasAdditionalLeaderboardRows ? (
                <button
                  type="button"
                  className="fishipedia-rankings-full-button"
                  onClick={() => setIsLeaderboardExpanded(true)}
                  aria-expanded={isLeaderboardExpanded}
                >
                  <Trophy size={17} strokeWidth={2.4} aria-hidden="true" />
                  <span>
                    {t("fishipedia_rankings_view_full_leaderboard", {
                      defaultValue: "View full leaderboard",
                    })}
                  </span>
                  <ChevronRight
                    size={18}
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                </button>
              ) : null}
              {expandedLeaderboardRows.map((user, index) => {
                const rank = index + 6;

                return (
                  <LeaderboardRow
                    key={user.id || user.username || rank}
                    user={user}
                    rank={rank}
                    t={t}
                    isCurrentUser={isSameRankedUser(user, currentUser)}
                  />
                );
              })}
            </div>
          </>
        )}
        </section>
      </div>

      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}

      {isInfoOpen ? (
        <div
          className="fishipedia-rankings-info-backdrop"
          role="presentation"
          onClick={() => setIsInfoOpen(false)}
        >
          <section
            className="fishipedia-rankings-info"
            role="dialog"
            aria-modal="true"
            aria-label={t("fishipedia_rankings_info_dialog_label", {
              defaultValue: "User rankings information",
            })}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="fishipedia-rankings-info-header">
              <h2>
                {t("fishipedia_rankings_info_title", {
                  defaultValue: "How rankings work",
                })}
              </h2>
              <button
                type="button"
                className="fishipedia-rankings-info-close"
                onClick={() => setIsInfoOpen(false)}
                aria-label={t("common_close", { defaultValue: "Close" })}
              >
                <X size={18} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </div>
            <div className="fishipedia-rankings-info-body">
              <p className="fishipedia-rankings-info-intro">
                {t("fishipedia_rankings_info_intro", {
                  defaultValue:
                    "Rankings are based on XP. The more useful fishing activity and catch detail you share, the more XP you can earn.",
                })}
              </p>
              <div className="fishipedia-rankings-info-sections">
                <section className="fishipedia-rankings-info-section">
                  <h3>
                    {t("fishipedia_rankings_info_how_title", {
                      defaultValue: "How you earn XP",
                    })}
                  </h3>
                  <ul>
                    {INFO_HOW_ITEMS.map((key) => (
                      <li key={key}>{t(key)}</li>
                    ))}
                  </ul>
                </section>
                <section className="fishipedia-rankings-info-section">
                  <h3>
                    {t("fishipedia_rankings_info_catch_details_title", {
                      defaultValue: "Catch details XP",
                    })}
                  </h3>
                  <div className="fishipedia-rankings-info-grid">
                    {INFO_CATCH_DETAIL_ITEMS.map((key) => (
                      <span key={key}>{t(key)}</span>
                    ))}
                  </div>
                </section>
                <section className="fishipedia-rankings-info-section">
                  <h3>
                    {t("fishipedia_rankings_info_engagement_title", {
                      defaultValue: "Post engagement XP",
                    })}
                  </h3>
                  <div className="fishipedia-rankings-info-grid">
                    {INFO_ENGAGEMENT_ITEMS.map((key) => (
                      <span key={key}>{t(key)}</span>
                    ))}
                  </div>
                </section>
                <section className="fishipedia-rankings-info-section">
                  <h3>
                    {t("fishipedia_rankings_info_good_to_know_title", {
                      defaultValue: "Good to know",
                    })}
                  </h3>
                  <ul>
                    {INFO_GOOD_TO_KNOW_ITEMS.map((key) => (
                      <li key={key}>{t(key)}</li>
                    ))}
                  </ul>
                </section>
              </div>
              <button
                type="button"
                className="fishipedia-rankings-info-bottom-close"
                onClick={() => setIsInfoOpen(false)}
              >
                {t("common_close", { defaultValue: "Close" })}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
